import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 shadow-sm">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Left: logo + desktop nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="text-xl font-bold tracking-tight text-stone-900 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading, serif)' }}>
              AllChinaBuy
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm font-medium text-stone-800 hover:text-stone-900 transition-colors whitespace-nowrap">
                {t('nav.home')}
              </Link>
              <div className="relative" ref={catRef}>
                <button
                  onClick={() => setCatOpen(!catOpen)}
                  className="text-sm font-medium text-stone-800 hover:text-stone-900 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  {t('nav.categories')}
                  <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                </button>
                {catOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg border border-stone-200 shadow-lg py-2 max-h-[70vh] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 transition-colors"
                      >
                        <span className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-stone-100">
                          <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/allchinabuy-beginner-guide"
                className="text-sm font-medium text-stone-800 hover:text-stone-900 transition-colors whitespace-nowrap"
              >
                {t('nav.shoppingGuide')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-medium text-stone-800 hover:text-stone-900 transition-colors whitespace-nowrap"
              >
                {t('nav.spreadsheet')}
              </a>
            </div>
          </div>

          {/* Right: language + mobile menu */}
          <div className="flex items-center gap-3">
            <div className="relative hidden sm:block" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="text-sm font-medium text-stone-800 hover:text-stone-900 transition-colors flex items-center gap-1.5"
              >
                <span className="text-base">{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-lg border border-stone-200 shadow-lg py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLang(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                        i18n.language === lang.code ? 'bg-stone-100 text-stone-900' : 'text-stone-700 hover:bg-stone-50'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Menu"
            >
              <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium text-stone-900 hover:bg-stone-50 transition-colors"
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-stone-500" />
              {t('nav.home')}
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-base font-medium text-stone-900 hover:bg-stone-50 transition-colors"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line w-5 h-5 flex items-center justify-center text-stone-500" />
                {t('nav.categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileCatOpen && (
              <div className="pl-4 space-y-0.5 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    <span className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 bg-stone-100">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/allchinabuy-beginner-guide"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium text-stone-900 hover:bg-stone-50 transition-colors"
            >
              <i className="ri-book-open-line w-5 h-5 flex items-center justify-center text-stone-500" />
              {t('nav.shoppingGuide')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-base font-medium text-stone-900 hover:bg-stone-50 transition-colors"
            >
              <i className="ri-external-link-line w-5 h-5 flex items-center justify-center text-stone-500" />
              {t('nav.spreadsheet')}
            </a>

            {/* Language switcher inside mobile menu */}
            <div className="pt-3 mt-3 border-t border-stone-200">
              <p className="px-3 pb-2 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                {t('nav.language')}
              </p>
              <div className="grid grid-cols-2 gap-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLang(lang.code)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      i18n.language === lang.code ? 'bg-stone-100 text-stone-900 font-medium' : 'text-stone-700 hover:bg-stone-50'
                    }`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}